import { Button, Flex, Select } from 'antd'
import React, { useEffect, useReducer, useState } from 'react'
import { Toggle } from '../../../../components';
import PrimaryRoofAttributes from './PrimaryRoofAttributes';
import AdditionalRoofAttributes from './AdditionalRoofAttributes';
import { useViewerStore } from '../../../../store/store';
import { BuildingDesignData, DesignData, RoofDesignData } from '../../../../@types/types';
import cloneDeep from 'lodash/cloneDeep';
import InactiveDesignList from './InactiveDesignList';

interface RoofPropertiesPopupProps {
    selectedBuilding: BuildingDesignData,
    handleCopy?: (index: number, roofData: RoofDesignData | null) => void;
    initialRoofIndex?: number;
    initialShowAdditional?: boolean;
}

const RoofPropertiesPopup: React.FC<RoofPropertiesPopupProps> = ({
    selectedBuilding,
    handleCopy,
    initialRoofIndex = 0,
    initialShowAdditional = false,
}) => {
    const { oldDesignList } = useViewerStore();
    const [showAdditionalRoofsProperties, toggleShowAdditionalRoofs] = useReducer(state => !state, initialShowAdditional);
    const [selectedRoofIndex, setSelectedRoofIndex] = useState(initialRoofIndex);
    const [designData, setDesignData] = useState<DesignData[]>(cloneDeep(oldDesignList || []));

    // get current building index
    const [currentBuildingIdx, setCurrentBuildingIdx] = useState(() => {
        const index = designData[0]?.Buildings?.findIndex((b: any) => b.Id === selectedBuilding?.Id);
        return index !== undefined && index !== -1 ? index : 0;
    });

    const sceneViewer = useViewerStore(state => state.sceneViewer);

    useEffect(() => {
        if (designData.length > 0) {
            const activeB = designData[0].Buildings[currentBuildingIdx];
            if (activeB && (!activeB.Roofs || selectedRoofIndex >= activeB.Roofs.length)) {
                setSelectedRoofIndex(0);
            }
        }
    }, [designData, currentBuildingIdx]);

    const updateRoofProperties = () => {
        const attributeKeys = [
            'Rafter_Size', 'Existing_Rafter_Qty', 'Unsupported_Rafter_Span',
            'Rafter_Spacing', 'Max_Span', 'Roof_Type_Id', 'Structure_Type_Id', 'Roof_Condition_Id',
            'Notes', 'Questionable_Roof', 'Questionable_Roof_Id', 'Questionable_Roof_Notes',
            'Roof_Has_Attic_Acces', 'Existing_Solar_Panels', 'Existing_Solar_Area',
            'PV_Module_Type', 'Rail_Manufacturer', 'Rail_Type', 'Roof_Covering', 'Attachment_Manufacturer',
            'Attachment_Type', 'Number_Of_Stories', 'Existing_Damage', 'Truss_Type'
        ];
        const activeBuilding = designData.length > 0 ? designData[0].Buildings[currentBuildingIdx] : null;
        const selectedRoofData: RoofDesignData | null = activeBuilding ? activeBuilding.Roofs[selectedRoofIndex]: null;
        let updatedRoof: any = selectedBuilding ? cloneDeep(selectedBuilding.Roofs[initialRoofIndex]) : null;
        if (updatedRoof && selectedRoofData) {
            attributeKeys.forEach((key) => {   // Object.keys(updatedRoof)
                updatedRoof[key] = selectedRoofData[key as keyof typeof selectedRoofData]
            });
        }
        handleCopy?.(initialRoofIndex, updatedRoof);
        cancel();
    }
    console.log('RoofPropertiesPopup State:', { designData, currentBuildingIdx, selectedRoofIndex });
    const handleBuildingChange = (index: number) => {
        if (designData[0]?.Buildings && index >= 0 && index < designData[0].Buildings.length) {
            setCurrentBuildingIdx(index);
            setSelectedRoofIndex(0); // Reset roof selection when building changes
        }
    }

    const cancel = () => {
        //Close Alert
        sceneViewer.viewerAlert({ show: false });
    };

    const activeBuilding = designData.length > 0 ? designData[0].Buildings[currentBuildingIdx] : null;
    // Filter roofs for the right side to show only the selected one
    const updatedBuildingData = activeBuilding ? {
        ...activeBuilding,
        Roofs: [activeBuilding.Roofs[selectedRoofIndex]]
    } : undefined;
    const handleSingleRoofUpdate = (update: any) => {
        if (!update || designData.length === 0) return;

        setDesignData(prev => {
            if (!prev || prev.length === 0) return prev;
            const newData = cloneDeep(prev);

            // Get current state to pass to functional update
            const currentBuilding = newData[0].Buildings[currentBuildingIdx];
            const singleBuildingState = {
                ...currentBuilding,
                Roofs: [currentBuilding.Roofs[selectedRoofIndex]]
            };

            let updatedSingleBuilding;
            if (typeof update === 'function') {
                updatedSingleBuilding = update(singleBuildingState);
            } else {
                updatedSingleBuilding = update;
            }

            if (!updatedSingleBuilding || !updatedSingleBuilding.Roofs) return prev;

            newData[0].Buildings[currentBuildingIdx].Roofs[selectedRoofIndex] = updatedSingleBuilding.Roofs[0];
            return newData;
        });
    };



    return (
        <div className="roof-properties-popup-container" style={{ width: '1200px', height: '600px', display: 'flex', flexDirection: 'column', color: 'white', padding: '20px' }}>
            <Flex style={{ flex: 1, overflow: 'hidden' }} gap={20}>
                <div style={{ flex: '0 0 30%', height: '100%', overflowY: 'auto' }} className="popup-column">
                    <div className="design-list-wrapper">
                        <InactiveDesignList
                            onSelectDesign={(data: DesignData) => setDesignData([data])}
                        />
                    </div>
                </div>
                <div style={{ flex: '0 0 30%', height: '100%', overflowY: 'auto' }} className="popup-column">
                    <div className="column-header-dropdown">
                        {designData.length > 0 && (
                            <Select
                                className="building-selection-dropdown"
                                value={currentBuildingIdx}
                                onChange={handleBuildingChange}
                                style={{ width: '100%' }}
                            >
                                {designData[0].Buildings.map((item, index) => (
                                    <Select.Option key={index} value={index}>
                                        {item.Building_Name || `Building ${index + 1}`}
                                    </Select.Option>
                                ))}
                            </Select>
                        )}
                    </div>

                    <div className="roof-buttons-list" style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
                        {activeBuilding?.Roofs.map((roof: any, index) => (
                            <div
                                key={index}
                                className={`roof-selection-tile ${selectedRoofIndex === index ? 'active' : ''}`}
                                onClick={() => setSelectedRoofIndex(index)}
                                style={{
                                    padding: '10px',
                                    borderRadius: '8px',
                                    background: selectedRoofIndex === index ? 'white' : '#2a2a2a',
                                    color: selectedRoofIndex === index ? 'black' : 'white',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    border: '1px solid #444',
                                    fontWeight: '500'
                                }}
                            >
                                {selectedRoofIndex === index && <span style={{ color: '#4a89bc' }}>✔</span>}
                                {roof.Name_Override ? `Roof ${roof.Name_Override}` : roof.Name}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right Column: Roof Attributes (40%) */}
                {/* <div style={{ width: '40%', height: '100%', overflowY: 'auto', paddingLeft: '10px' }} className="popup-column"> */}
                <div style={{ flex: '1', height: '100%', overflowY: 'auto', paddingLeft: '10px' }} className="popup-column">
                    <Flex align="center" justify="space-between" style={{ padding: "0px 20px 15px 20px" }}>
                        <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#aaa' }}>Additional Roof Properties</span>
                        <Toggle
                            on={showAdditionalRoofsProperties}
                            onChange={toggleShowAdditionalRoofs}
                        />
                    </Flex>
                    <div className="attributes-form-wrapper">
                        {activeBuilding && (
                            !showAdditionalRoofsProperties ? (
                                <PrimaryRoofAttributes
                                    selectedBuilding={updatedBuildingData as any}
                                    setSelectedBuilding={handleSingleRoofUpdate as any}
                                    showInactive={false}
                                    readOnly={false}
                                    isFromPopup={true}
                                />
                            ) : (
                                <AdditionalRoofAttributes
                                    selectedBuilding={updatedBuildingData as any}
                                    setSelectedBuilding={handleSingleRoofUpdate as any}
                                    showInactive={false}
                                    readOnly={false}
                                    isFromPopup={true}
                                />
                            )
                        )}
                    </div>
                </div>
            </Flex>

            {/* Bottom Actions */}
            <div style={{
                marginTop: '20px',
                borderTop: '1px solid #3e3e3e',
                paddingTop: '20px',
                display: 'flex',
                justifyContent: 'center',
                gap: '20px'
            }}>
                <Button
                    id="roofPopupUpdateBtn"
                    type="primary"
                    size="large"
                    onClick={updateRoofProperties}
                >
                    COPY DATA
                </Button>
                <Button
                    type="default"
                    size="large"
                    danger
                    ghost
                    onClick={cancel}
                >
                    CANCEL
                </Button>
            </div>
        </div>
    );
};

export default RoofPropertiesPopup